import { window } from '../window.js';
import { AppParams } from './app_params.js';
import { assert, log } from './logger.js';
import { PageHistory } from './page_history.js';
import { settings } from './settings.js';

class Navigation {
  // Panel ids are defined here to allow panel lazy loading. The panel is loaded
  // and initialized when requested.
  kCancelPwdRecoveryPanelId = 'cancel-pwd-recovery';
  kContactUsPanelId = 'contact-us';
  kGalleryPanelId = 'gallery';
  kLocPanelId = 'loc';
  kLoginPanelId = 'login';
  kMapPanelId = 'maps';
  kPicUploadPanelId = 'picupload';
  kProfilePanelId = 'profile';
  kResetPwdPanelId = 'reset-pwd';
  kSignupPanelId = 'signup';
  kStorePanelId = 'store';
  kTripPlanningPanelId = 'trip-planning';
  kTripPanelId = 'trip';

  kNotRestorablePanelIds = [
    this.kLoginPanelId,
    this.kResetPwdPanelId,
    this.kSignupPanelId,
  ];

  exploreMap(locId, hostPanel = this.kTripPlanningPanelId) {
    this.openMap({
      consumerId: 'explore-maps-create-trip',
      hostPanel,
      locId,
    });
  }

  openMap(options) {
    log(
      `[navigation] open map, host panel is ${options.hostPanel || 'unknown'}`
    );
    return this.toPanel(this.kMapPanelId, options);
  }

  closeMap() {
    let hostPanel = this.#getPanel(this.kMapPanelId).params.hostPanel;
    if (this.#getPanel(hostPanel)) {
      log(`[navigation] close map, back to ${hostPanel}`);
      return this.toPanel(hostPanel);
    }

    // User is not logged-in. Move to /intro page
    log(`[navigation] close map, user is not logged-in, back to intro/ page`);
    window.location = '/intro';
    return null;
  }

  openGallery(options) {
    return this.toPanel(this.kGalleryPanelId, options);
  }

  closeGallery() {
    return this.closePanel(this.kGalleryPanelId);
  }

  openPicUpload(options) {
    return this.toPanel(this.kPicUploadPanelId, options);
  }

  closePicUpload() {
    return this.closePanel(this.kPicUploadPanelId);
  }

  openStore(options) {
    return this.toPanel(this.kStorePanelId, options);
  }

  closeStore() {
    return this.closePanel(this.kStorePanelId);
  }

  toLoc(options) {
    return this.toPanel(this.kLocPanelId, options);
  }

  toTripPlanning() {
    return this.toPanel(this.kTripPlanningPanelId);
  }

  toTrip(options) {
    return this.toPanel(this.kTripPanelId, options);
  }

  toProfile() {
    return this.toPanel(this.kProfilePanelId);
  }

  toLogin() {
    return this.toPanel(this.kLoginPanelId);
  }

  toSignup() {
    return this.toPanel(this.kSignupPanelId);
  }

  toContactUs() {
    return this.toPanel(this.kContactUsPanelId);
  }

  get map() {
    return this.#getPanel(this.kMapPanelId);
  }

  get loc() {
    return this.#getPanel(this.kLocPanelId);
  }

  get picUpload() {
    return this.#getPanel(this.kPicUploadPanelId);
  }

  get profile() {
    return this.#getPanel(this.kProfilePanelId);
  }

  get tripPlanning() {
    return this.#getPanel(this.kTripPlanningPanelId);
  }

  get trip() {
    return this.#getPanel(this.kTripPanelId);
  }

  get activePanel() {
    return this.#activePanel;
  }

  async toPanel(id, options, skipHistory) {
    log(
      `[navigation] switch to ${id} panel, options: ${JSON.stringify(
        options
      )}, ${skipHistory ? 'no' : 'do'} history`
    );

    // Make sure the stub panel is hidden.
    let stubPanel = window.document.getElementById('panel-stub');
    if (stubPanel) {
      stubPanel.hidden = true;
    }

    // Hide currently active panel.
    if (this.#activePanel) {
      await this.#activePanel.switchOff();
      assert(
        !this.#activePanel.active,
        `${this.#activePanel.constructor.id} panel is active still`
      );
    }

    // Activate panel.
    this.#activePanel = this.#getPanel(id);
    await this.#activePanel.switchOn(options);
    assert(
      this.#activePanel.active,
      `${this.#activePanel.constructor.id} panel is not active`
    );

    let title = this.#activePanel.historyState[1];
    if (title) {
      window.document.title = title;
    }

    return (!skipHistory && this.#history?.toPage(id, options)) || null;
  }

  closePanel(id) {
    log(`[navigation] close '${id}' panel`);
    return this.#history?.back();
  }

  restoreSession(session) {
    // Do nothing if history was changed.
    if (!this.#history || this.#history.stateChanged) {
      return;
    }

    // Load maps or location if requested.
    let isSwitched = false;
    let { page } = this.params;
    switch (page) {
      case this.kMapPanelId:
        {
          let locId =
            this.params.locId ||
            (this.#history.stateParams && this.#history.stateParams.locId);
          this.exploreMap(locId);
          isSwitched = true;
        }
        break;
      case this.kLocPanelId:
        this.toLoc({
          locId: this.params.locId,
        });
        isSwitched = true;
        break;
      case this.kStorePanelId:
        this.openStore();
        isSwitched = true;
        break;
      default:
        break;
    }

    // Maps and loc are handled separately.
    if (isSwitched) {
      return;
    }

    // If logged-in, then no restore non-restorable pages like 'signup' or
    // 'login'.
    if (session && this.kNotRestorablePanelIds.includes(page)) {
      return;
    }

    // Switсh to requested page or show login page if not logged in.
    if (!page && !session) {
      page = this.kLoginPanelId;
    }
    if (page) {
      this.toPanel(
        page,
        null,
        this.#history.isPage(page) ? 'no-history' : null
      );
      this.#history.setStateChanged(); // avoid history state restore
    }
  }

  setPanelOnLogin() {
    // Restore active page if exists and if no extant page was requested,
    // otherwise load default trips panel. In case if extant page was requested,
    // the caller has to load the page itself.
    let prevPage = this.#history?.restorableState?.page;
    log(
      `[navigation] restore history if any, prev page: ${prevPage}, isExtantPage: ${this.params.isExtantPage}`
    );
    if (
      !this.params.isExtantPage &&
      (this.kNotRestorablePanelIds.includes(prevPage) ||
        !this.#history?.restoreState())
    ) {
      this.toTripPlanning();
    }
  }

  /**
   * Returns the application params extracted from the document location search
   * params.
   */
  get params() {
    if (!this.#params) {
      this.#params = new AppParams();
    }
    return this.#params;
  }

  set params(paramsObj) {
    assert(!this.#params, `[navigation] app params was already set`);
    this.#params = paramsObj;
  }

  get historyState() {
    return this.#history?.state;
  }

  setHistoryTo(page) {
    return this.#history?.toPage(page);
  }

  replaceHistoryTo(page) {
    return this.#history?.replacePage(page);
  }

  /**
   * Panel self-registration. Each app panel has to register itself.
   **/
  registerPanel(Panel) {
    assert(Panel, `[navigation] register panel: no panel`);
    assert(Panel.id, `[navigation] register panel: no panel id`);
    assert(
      !this.#panelClasses[Panel.id],
      `[navigation] register panel: ${Panel.id} already registered`
    );
    log(`[navigation] register '${Panel.id}' panel`);
    this.#panelClasses[Panel.id] = { Panel };
    window.customElements.define(Panel.tag, Panel);
  }

  findPanel(historyPage) {
    for (let id in this.#panelClasses) {
      const { Panel } = this.#panelClasses[id];
      if (Panel.ownsPage(historyPage)) {
        return this.#getPanel(Panel.id);
      }
    }
    return null;
  }

  #getPanel(id) {
    if (!(id in this.#panels)) {
      assert(
        id in this.#panelClasses,
        `[navigation] no '${id}' panel registered`
      );
      const { Panel } = this.#panelClasses[id];
      let panelElement = window.document.querySelector(Panel.tag);
      if (!panelElement) {
        panelElement = window.document.createElement(Panel.tag);
        let anchorElement = window.document.body.querySelector('.panel-anchor');
        anchorElement.before(panelElement);
      }
      panelElement.prepare?.();
      this.#panels[id] = panelElement;
    }
    return this.#panels[id];
  }

  get #history() {
    if (!this.#historyInstance && !settings.historyDisabled) {
      this.#historyInstance = new PageHistory();
    }
    return this.#historyInstance;
  }

  #activePanel;
  #historyInstance;
  #panelClasses = {};
  #panels = {};
  #params;
}

export const navigation = new Navigation();
